import { ISearchInput } from '../types';
import { IDateRangeInput, Maybe } from '../../../../utils/apollo/graphqlSchema';
import moment from 'moment/moment';
import { INTERNAL_DATE_FORMAT } from '../../../../config/constants';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { TextButton } from '../../../../components/Button';
import { SvgIcon } from '../../../../components/Icon';
import Pill from '../../../../components/Pill';
import * as AdvancedSearchMenuUI from './AdvancedSearchMenuUI';

interface IProps {
  searchInput: ISearchInput;
  setSearchInput: (searchInput: ISearchInput, isLoadMore: boolean) => void;
  onReset: () => void;
}

const Header = styled.div`
  display: flex;
  column-gap: 4px;
  justify-content: space-between;
`;

const LastUpdatedSearchOption = ({
  searchInput,
  setSearchInput,
  onReset,
}: IProps) => {
  const today = moment().format(INTERNAL_DATE_FORMAT);
  const getDateByValueSubtract = useCallback(
    (value: number) => {
      return moment(today, true)
        .subtract(value, 'days')
        .format(INTERNAL_DATE_FORMAT);
    },
    [today],
  );
  const lastUpdatedOptions: {
    label: string;
    value: Maybe<IDateRangeInput>;
  }[] = useMemo(
    () => [
      {
        label: 'Any Time',
        value: null,
      },
      {
        label: 'Today',
        value: {
          start: today,
          end: today,
        },
      },
      {
        label: 'Yesterday',
        value: {
          start: getDateByValueSubtract(1),
          end: getDateByValueSubtract(1),
        },
      },
      {
        label: 'Past 7 days',
        value: {
          start: getDateByValueSubtract(7),
          end: today,
        },
      },
      {
        label: 'Past 30 days',
        value: {
          start: getDateByValueSubtract(30),
          end: today,
        },
      },
    ],
    [getDateByValueSubtract, today],
  );

  const checkIsDateRangeEqual = (
    value1: Maybe<IDateRangeInput>,
    value2: Maybe<IDateRangeInput>,
  ) => {
    if (value1 === null && value2 === null) {
      return true;
    }
    if (value1 === null || value2 === null) {
      return false;
    }
    return value1.start === value2.start && value1.end === value2.end;
  };

  const onDateRangeItemClick = (dateRange: Maybe<IDateRangeInput>) => {
    setSearchInput(
      {
        ...searchInput,
        lastUpdateDateRange: dateRange,
      },
      false,
    );
  };
  return (
    <AdvancedSearchMenuUI.SectionContainer>
      <Header>
        <AdvancedSearchMenuUI.SectionHeading>
          Last Updated
        </AdvancedSearchMenuUI.SectionHeading>
        <TextButton type="button" onClick={onReset}>
          Reset
          <SvgIcon
            name="arrow-counter-clockwise"
            width={16}
            height={16}
            customFill="currentColor"
          />
        </TextButton>
      </Header>
      <AdvancedSearchMenuUI.SectionList>
        {lastUpdatedOptions.map((option) => {
          return (
            <Pill
              key={option.label}
              isActive={checkIsDateRangeEqual(
                searchInput.lastUpdateDateRange,
                option.value,
              )}
              onClick={() => onDateRangeItemClick(option.value)}
            >
              {option.label}
            </Pill>
          );
        })}
      </AdvancedSearchMenuUI.SectionList>
    </AdvancedSearchMenuUI.SectionContainer>
  );
};

export default LastUpdatedSearchOption;
